import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import CertificateLink from '../components/CertificateLink';
import { PasteModal, CopyFromAttendanceModal } from '../components/BatchAddPeople';
import { overlayDismiss } from '../utils/modalDismiss';
import { download, openPdf, formatDate, emailSelected, AWARD_NAMES, confirmIssue, certCounts } from '../utils/certificates';

// Create or edit a batch's course details and templates.
export const BatchSettingsModal = ({ batch, onClose, onSaved }) => {
  const [form, setForm] = useState({
    title: batch?.title || '', dates_text: batch?.dates_text || '', company: batch?.company || '',
    template_id: batch?.template_id || '', attendance_template_id: batch?.attendance_template_id || ''
  });
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/certificates/templates').then(r => setTemplates(r.data)).catch(() => {}); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = batch
        ? await api.put(`/certificate-batches/${batch.id}`, form)
        : await api.post('/certificate-batches', form);
      onSaved(data);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="modal-overlay" {...overlayDismiss(onClose)}>
      <div className="modal" style={{ maxWidth: 620 }}>
        <div className="modal-header">
          <h3>{batch ? 'Batch details' : 'New certificate batch'}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={save}>
          <div className="modal-body">
            <div className="form-group">
              <label>Course title</label>
              <input type="text" required maxLength={255} value={form.title} onChange={set('title')}
                placeholder="e.g. Root Cause Investigations in Pharma Industry" />
              <small className="text-muted">Printed wherever the template uses {'{course}'}</small>
            </div>
            <div className="grid grid-2">
              <div className="form-group">
                <label>Dates</label>
                <input type="text" maxLength={100} value={form.dates_text} onChange={set('dates_text')} placeholder="e.g. 5–6 May 2025" />
              </div>
              <div className="form-group">
                <label>Company (customer)</label>
                <input type="text" maxLength={255} value={form.company} onChange={set('company')} placeholder="e.g. Dr. Reddy's Laboratories Ltd" />
              </div>
            </div>
            <div className="grid grid-2">
              <div className="form-group">
                <label>Completion certificate</label>
                <select value={form.template_id} onChange={set('template_id')}>
                  <option value="">None</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Attendance certificate</label>
                <select value={form.attendance_template_id} onChange={set('attendance_template_id')}>
                  <option value="">None</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            </div>
            {batch && <p className="text-sm text-muted">Changes apply to certificates issued from now on.</p>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : batch ? 'Save' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PersonModal = ({ person, onClose, onSave }) => {
  const [form, setForm] = useState({ name: person?.name || '', email: person?.email || '', employee_id: person?.employee_id || '', award: person?.award || 'attendance' });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="modal-overlay" {...overlayDismiss(onClose)}>
      <div className="modal">
        <div className="modal-header">
          <h3>{person ? 'Edit person' : 'Add person'}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSave(form); }}>
          <div className="modal-body">
            <div className="form-group"><label>Name (as printed)</label><input type="text" required maxLength={255} value={form.name} onChange={set('name')} autoFocus /></div>
            <div className="form-group"><label>Email</label><input type="email" maxLength={255} value={form.email} onChange={set('email')} /></div>
            <div className="form-group"><label>Employee ID (optional)</label><input type="text" maxLength={50} value={form.employee_id} onChange={set('employee_id')} /></div>
            {!person && (
              <div className="form-group">
                <label>Certificates</label>
                <select value={form.award} onChange={set('award')}>
                  {Object.entries(AWARD_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CertificateBatch = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(false);
  const [person, setPerson] = useState(null); // {} new, or a person
  const [busy, setBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importMode, setImportMode] = useState('column');
  const [adding, setAdding] = useState(null); // 'paste' | 'copy'
  const fileRef = useRef();

  const load = useCallback(() => {
    api.get(`/certificate-batches/${id}`)
      .then(r => { setBatch(r.data); setError(''); })
      .catch(err => setError(err.response?.data?.error || 'Failed to load batch'));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!batch) return error ? <div className="alert alert-error">{error}</div> : <div className="text-center mt-5">Loading...</div>;

  const people = batch.people;
  const chosen = people.filter(p => selected.has(p.id));
  const toggle = (pid) => setSelected(s => { const n = new Set(s); if (n.has(pid)) n.delete(pid); else n.add(pid); return n; });
  const levelsFor = (award) => ({ both: ['achievement', 'attendance'], attendance: ['attendance'], none: [] }[award]);
  const templateFor = { achievement: batch.template_id, attendance: batch.attendance_template_id };
  const toIssue = people.reduce((n, p) => n + levelsFor(p.award).filter(l => templateFor[l] && !p[l]).length, 0);
  const issuedCerts = (list) => list.flatMap(p => ['achievement', 'attendance'].filter(l => p[l]).map(l => ({
    ...p[l], level: l, recipient_name: p.name, recipient_email: p.email
  })));
  const allCerts = issuedCerts(people);

  const importFile = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    form.append('award_mode', importMode);
    setBusy(true);
    try {
      const { data } = await api.post(`/certificate-batches/${id}/import`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult({ ok: true, ...data });
      load();
    } catch (err) {
      setImportResult({ ok: false, error: err.response?.data?.error || 'Import failed', problems: err.response?.data?.problems });
    } finally {
      setBusy(false);
      fileRef.current.value = '';
    }
  };

  const setAward = async (ids, award) => {
    try {
      await api.put(`/certificate-batches/${id}/awards`, { person_ids: ids, award });
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save');
    }
  };

  const issue = async () => {
    const ach = people.filter(p => p.award === 'both' && batch.template_id && !p.achievement).length;
    const att = people.filter(p => p.award !== 'none' && batch.attendance_template_id && !p.attendance).length;
    const who = people.filter(p => (p.award === 'both' && batch.template_id && !p.achievement) || (p.award !== 'none' && batch.attendance_template_id && !p.attendance)).length;
    if (!confirmIssue(who, ach, att)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/certificate-batches/${id}/issue`, {});
      alert(`Issued: ${certCounts(data.achievement, data.attendance)}. Nothing has been emailed.`);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to issue');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${chosen.length} person(s) from this batch? People who already have certificates are kept — withdraw those first.`)) return;
    const { data } = await api.post(`/certificate-batches/${id}/people/remove`, { person_ids: chosen.map(p => p.id) });
    if (data.kept) alert(`${data.removed} removed. ${data.kept} kept because they have certificates.`);
    setSelected(new Set());
    load();
  };

  const savePerson = async (form) => {
    try {
      if (person.id) await api.put(`/certificate-batches/${id}/people/${person.id}`, form);
      else await api.post(`/certificate-batches/${id}/people`, form);
      const live = person.id ? ['achievement', 'attendance'].map(l => person[l]).filter(Boolean) : [];
      if (live.length && form.name.trim() !== person.name &&
          window.confirm(`${person.name} already has ${live.length} certificate(s) printed with the old name.\n\n` +
            `Reissue ${live.length > 1 ? 'them' : 'it'} as "${form.name.trim()}"? The old ${live.length > 1 ? 'ones show' : 'one shows'} as withdrawn when verified.`)) {
        for (const c of live) await api.post(`/certificates/${c.id}/reissue`, { recipient_name: form.name.trim(), reason: 'Name corrected' });
      }
      setPerson(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save');
    }
  };

  const deleteBatch = async () => {
    if (!window.confirm(`Delete batch "${batch.title}"?`)) return;
    try {
      await api.delete(`/certificate-batches/${id}`);
      navigate('/certificates?tab=batches');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete');
    }
  };

  const cell = (p, level) => {
    const wanted = levelsFor(p.award).includes(level) && templateFor[level];
    const c = p[level];
    if (c) {
      return (
        <span className="text-sm" style={{ whiteSpace: 'nowrap' }}>
          <CertificateLink cert={c} name={p.name} />
          {c.emailed_at && <span className="text-muted" title={`Emailed ${formatDate(c.emailed_at)}`}> ✉</span>}
          {!levelsFor(p.award).includes(level) && <div className="text-muted" style={{ color: '#b45309' }}>Issued earlier — withdraw it under Certificates › Issued if no longer wanted</div>}
        </span>
      );
    }
    if (wanted) return <span className="badge badge-info">Ready to issue</span>;
    return <span className="text-muted">—</span>;
  };

  return (
    <div>
      <Link to="/certificates?tab=batches" className="text-muted text-sm">← All imported lists</Link>
      <div className="flex flex-between flex-center mb-4 mt-2" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h1 style={{ marginBottom: 4 }}>{batch.title}</h1>
          <p className="text-muted">
            {[batch.dates_text, batch.company, `${people.length} people`].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>Edit details</button>
          {!allCerts.length && <button className="btn btn-danger btn-sm" onClick={deleteBatch}>Delete</button>}
        </div>
      </div>

      {(!batch.template_id && !batch.attendance_template_id) && (
        <div className="alert alert-info mb-4">Choose the completion and attendance templates under "Edit details" before issuing.</div>
      )}

      <div className="card mb-4">
        <h3 style={{ marginTop: 0 }}>1. Add trainees</h3>
        <p className="text-sm text-muted">
          Copy trainees from QR attendance, import an Excel / CSV file (Name, Email, optionally Employee ID and Result),
          or paste rows. Adding someone already in the list (same email) updates them instead of duplicating.
        </p>
        <div className="flex gap-2 mb-3" style={{ flexWrap: 'wrap' }}>
          <button className="btn btn-primary btn-sm" onClick={() => setAdding('copy')}>Copy from attendance</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setAdding('paste')}>Paste names</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setPerson({})}>+ Add one person</button>
        </div>
        <div className="flex flex-center gap-2" style={{ flexWrap: 'wrap', borderTop: '1px solid #f3f4f6', paddingTop: 12 }}>
          <span className="text-sm">Import a file — everyone in it gets:</span>
          <select value={importMode} onChange={e => setImportMode(e.target.value)}
            style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}>
            <option value="column">As its Result column says (blank = Attendance only)</option>
            {Object.entries(AWARD_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <label className="btn btn-secondary btn-sm" style={{ margin: 0 }}>
            {busy ? 'Working...' : 'Choose file…'}
            <input ref={fileRef} type="file" accept=".xlsx,.csv" hidden onChange={e => importFile(e.target.files[0])} />
          </label>
          <button className="btn btn-secondary btn-sm" onClick={() => download('/certificate-batches/sample', 'Certificate_trainees_sample.xlsx')}>Sample file</button>
        </div>
        {importResult && (importResult.ok ? (
          <div className="alert alert-success mt-3">
            {importResult.added} added, {importResult.updated} updated.
            {importResult.no_email > 0 && ` ${importResult.no_email} without email (they can't be emailed).`}
            {!importResult.had_result_column && ' No Result column — everyone new is "Attendance only"; set who passed in the table below.'}
          </div>
        ) : (
          <div className="alert alert-error mt-3">
            {importResult.error}
            {importResult.problems && <ul style={{ margin: '8px 0 0 18px' }}>{importResult.problems.map((x, i) => <li key={i}>{x}</li>)}</ul>}
          </div>
        ))}
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="flex flex-between flex-center" style={{ padding: '16px 20px', flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0 }}>2. Choose certificates, then issue</h3>
          <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
            <button className="btn btn-primary btn-sm" onClick={issue} disabled={busy || !toIssue}>Issue certificates ({toIssue})</button>
            <button className="btn btn-secondary btn-sm" disabled={!allCerts.length}
              onClick={() => download(`/certificate-batches/${id}/zip`, `Certificates_${batch.title.replace(/[^a-zA-Z0-9]+/g, '_')}.zip`)}>
              Download all (ZIP)
            </button>
          </div>
        </div>
        {people.length > 0 && (
          <div className="flex flex-center gap-2" style={{ padding: '0 20px 12px', flexWrap: 'wrap' }}>
            <span className="text-sm text-muted">{chosen.length} selected:</span>
            {Object.entries(AWARD_NAMES).map(([k, v]) => (
              <button key={k} className="btn btn-secondary btn-sm" disabled={!chosen.length} onClick={() => setAward(chosen.map(p => p.id), k)}>
                Set to {v}
              </button>
            ))}
            <button className="btn btn-secondary btn-sm" disabled={!issuedCerts(chosen).length}
              onClick={() => emailSelected(`/certificate-batches/${id}/email`, issuedCerts(chosen), () => { setSelected(new Set()); load(); })}>
              Email selected ({issuedCerts(chosen).length} certificates)
            </button>
            <button className="btn btn-danger btn-sm" disabled={!chosen.length} onClick={remove}>Remove</button>
          </div>
        )}
        {people.length === 0 ? (
          <p className="text-muted text-center" style={{ padding: '0 20px 20px' }}>No trainees yet. Import a file or add people above.</p>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input type="checkbox" checked={chosen.length === people.length}
                      onChange={e => setSelected(e.target.checked ? new Set(people.map(p => p.id)) : new Set())} title="Select all" />
                  </th>
                  <th>Name</th><th>Certificates</th><th>Completion</th><th>Attendance</th><th></th>
                </tr>
              </thead>
              <tbody>
                {people.map(p => (
                  <tr key={p.id}>
                    <td><input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} /></td>
                    <td>
                      <strong>{p.name}</strong>
                      <div className="text-sm text-muted">{[p.employee_id, p.email || 'no email'].filter(Boolean).join(' · ')}</div>
                    </td>
                    <td>
                      <select value={p.award} onChange={e => setAward([p.id], e.target.value)}
                        style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}>
                        {Object.entries(AWARD_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </td>
                    <td>{cell(p, 'achievement')}</td>
                    <td>{cell(p, 'attendance')}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => setPerson(p)}>Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-sm text-muted" style={{ padding: '0 20px 16px', margin: 0 }}>
          Issuing never emails anyone. Tick people, then "Email selected" to send their certificates.
          Editing a name after issuing offers to reissue the certificate with the new name.
        </p>
      </div>

      {editing && <BatchSettingsModal batch={batch} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}
      {adding === 'paste' && <PasteModal batchId={batch.id} onClose={() => setAdding(null)} onAdded={() => { setAdding(null); load(); }} />}
      {adding === 'copy' && (
        <CopyFromAttendanceModal batchId={batch.id} existingEmails={new Set(people.map(p => (p.email || '').toLowerCase()).filter(Boolean))}
          onClose={() => setAdding(null)} onAdded={() => { setAdding(null); load(); }} />
      )}
      {person && <PersonModal person={person.id ? person : null} onClose={() => setPerson(null)} onSave={savePerson} />}
    </div>
  );
};

export default CertificateBatch;
